import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { KeyHandlerService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject } from "../../../../interfaces";
import { CachedCodeLookupService, MetadataPropertyService, MetadataService } from "../../../../services";

@Component({
    selector: "metadata-rich-text",
    templateUrl: "./metadata-rich-text.tmpl.html",
    standalone: false
})
export class MetadataRichTextComponent implements OnInit, OnDestroy, DoCheck, OnChanges, AfterViewInit
{
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $cachedCodeLookupService = inject(CachedCodeLookupService);
    private $keyHandlerService = inject(KeyHandlerService);

    @Input()
    model: string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    viewOnly: boolean;

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    label: string;

    @Output()
    onValueChanged = new EventEmitter<any>();

    @ViewChild("enterToEdit")
    private enterToEdit: ElementRef;

    private isInitialized: boolean;

    Fields: string[] = [];

    id: string;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: string;

    private _valueModel: string;

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            !!((this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
                this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
                this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel));
    }

    private onModelChanged(value: string): void
    {
        this.unboundModel = value;
        if (!value)
        {
            this.valueModel = null;
        }
        else if (!this.valueModel || this.valueModel !== value)
        {
            this.valueModel = value;
        }
    }

    ngOnChanges(changesObj: SimpleChanges): void
    {
        const changedKeys = Object.keys(changesObj);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = changesObj[key];
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    ngOnInit(): void
    {
        this.isInitialized = true;
        this.unboundModel = this.model;
        this.id = `richText-${this.Name}`;
    }

    enterToEditKeydown(event: KeyboardEvent): void
    {
        if (event.code === "Enter")
        {
            this.toggleTabbable(true);
            this.quillEditor.root.focus();
            event.preventDefault();
        }
        else if (!event.shiftKey && event.code === "Tab")
        {
            this.toggleTabbable(false);
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }

    private _isViewOnly: boolean;
    get isViewOnly(): boolean
    {
        return this._isViewOnly;
    }

    set isViewOnly(value: boolean)
    {
        const reinit = value !== this._isViewOnly;
        this._isViewOnly = value;
        if (reinit)
        {
            this.initializeQuillEditor();
        }
    }

    quillEditor: any;
    private blurHandler = (): void => this.fireValueChanged();
    private focusHandler = (): void => this.editorFocused();
    private mousedownHandler = (e: MouseEvent): void => e.preventDefault();
    private escapeBinding: any;

    onQuillEditorCreated(e): void
    {
        this.quillEditor = e;
        this.initializeQuillEditor();
    }

    initializeQuillEditor(): void
    {
        if (!this.quillEditor)
        {
            return;
        }

        this.removeListeners();

        if (!this.isViewOnly)
        {
            this.quillEditor.root.addEventListener("blur", this.blurHandler);
            this.quillEditor.root.addEventListener("focus", this.focusHandler);
            this.quillEditor.getModule("toolbar").container.addEventListener("mousedown", this.mousedownHandler);

            const self = this;
            this.escapeBinding = this.quillEditor.keyboard.addBinding(
                { key: "Escape" },
                function (range, context)
                {
                    self.toggleTabbable(false);
                    self.enterToEdit.nativeElement.focus();
                }
            );
        }
        this.toggleTabbable(false);
        this.quillEditor.enable(!this.isViewOnly);
    }

    toggleTabbable(enable: boolean): void
    {
        if (this.quillEditor)
        {
            this.quillEditor.root.tabIndex = enable ? 0 : -1;
            const toolbarElements = document.querySelectorAll(`#${this.id} .ql-formats .ql-picker-label, #${this.id} .ql-formats button, #${this.id} .ql-formats forge-select`);
            toolbarElements?.forEach(x => (x as HTMLElement).tabIndex = enable ? 0 : -1);
        }
    }

    editorFocused(): void
    {
        if (!this.isViewOnly)
        {
            this.toggleTabbable(true);
        }
    }

    ngOnDestroy(): void
    {
        this.removeListeners();
        this.quillEditor = null;
        this.enterToEdit = null;
    }

    private removeListeners(): void
    {
        if (!this.quillEditor)
            return;

        if (this.quillEditor.root)
        {
            this.quillEditor.root.removeEventListener("blur", this.blurHandler);
            this.quillEditor.root.removeEventListener("focus", this.focusHandler);
        }

        const toolbar = this.quillEditor.getModule?.("toolbar");
        if (toolbar?.container)
            toolbar.container.removeEventListener("mousedown", this.mousedownHandler);

        if (this.escapeBinding && this.quillEditor.keyboard)
            this.escapeBinding = null;
    }

    get hasLookup(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.propertyMetadata.Lookup);
    }

    ngAfterViewInit(): void
    {
        if (!this.hasLookup)
            return;
        this.$cachedCodeLookupService.Get(this.propertyMetadata.Lookup, null)
            .then(result =>
            {
                if (Utilities.IsNullOrUndefined(result)) return;
                result.forEach(i =>
                {
                    this.Fields.push(i.Value);
                });
            });
    }

    appendOption(field: string): void
    {
        const insertedText = `{${field}}`;
        const insertedHtml = `{<span style="background-color: rgba(211, 211, 211, 1)">${field}</span>}`;
        const index = this.quillEditor.getSelection()?.index;
        if (Utilities.IsNullOrUndefined(this.valueModel))
            this.valueModel = "";
        if (index)
        {
            this.quillEditor.insertText(index, insertedText);
            this.quillEditor.root.innerHTML = this.quillEditor.root.innerHTML.replace(insertedText, insertedHtml);
            this.valueModel = this.quillEditor.root.innerHTML;
        }
        else
            this.valueModel += insertedHtml;

        this.fireValueChanged();
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        this._valueModel = value;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get isRequired(): boolean
    {
        return (
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) &&
            !this.isViewOnly
        );
    }

    fireValueChanged(): void
    {
        if (this.valueModel === undefined || this.model === this.valueModel || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: this.valueModel });
    }
}
